import React, { useEffect, useState } from "react";
import { Save, PhoneCall, Link as LinkIcon, Shield, Server } from "lucide-react";
import api, { API } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function SipConfigure() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { (async () => { const r = await api.get("/settings"); setS(r.data); })(); }, []);
  if (!s) return <div className="text-slate-500">{t("common.loading")}</div>;

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setBool = (k) => (e) => setS({ ...s, [k]: !!e.target.checked });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setSaved(false);
    const { tenant_id, updated_at, ...payload } = s;
    const r = await api.put("/settings", payload);
    setS(r.data); setBusy(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const Section = ({ icon: Icon, title, children }) => (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-3 pb-4 border-b border-white/5">
        <div className="h-9 w-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center"><Icon className="h-4 w-4 text-magenta" /></div>
        <h3 className="text-base font-bold text-white">{title}</h3>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );

  const Field = ({ label, children, full }) => (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );

  const backendBase = (API || "").replace(/\/api\/?$/, "");

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="sip-config-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="chip"><PhoneCall className="h-3 w-3" /> {t("sip.title")}</div>
          <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-white tracking-tight">{t("sip.title")}</h1>
          <p className="mt-1 text-slate-400 text-sm">{t("sip.sub")}</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-emerald-400 text-sm font-semibold">{t("settings.saved")}</span>}
          <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2">
            <Save className="h-4 w-4" /> {t("settings.save")}
          </button>
        </div>
      </div>

      <Section icon={LinkIcon} title={t("sip.twilio_section")}>
        <Field label={t("sip.twilio_number")}>
          <input value={s.twilio_phone_number || ""} onChange={set("twilio_phone_number")} className="input-dark font-mono" placeholder="+1XXXXXXXXXX" />
        </Field>
        <Field label={t("sip.twilio_sip_domain")}>
          <input value={s.twilio_sip_domain || ""} onChange={set("twilio_sip_domain")} className="input-dark font-mono" placeholder="yourdomain.sip.twilio.com" />
        </Field>
        <Field label={t("sip.twilio_sip_username")}>
          <input value={s.twilio_sip_username || ""} onChange={set("twilio_sip_username")} className="input-dark font-mono" placeholder="sip-user" />
        </Field>
        <Field label={t("sip.twilio_sip_password")}>
          <input type="password" value={s.twilio_sip_password || ""} onChange={set("twilio_sip_password")} className="input-dark font-mono" />
        </Field>
        <Field label={t("sip.inbound_webhook")} full>
          <input value={s.twilio_inbound_webhook_url || ""} onChange={set("twilio_inbound_webhook_url")} className="input-dark font-mono" placeholder={`${backendBase}/api/twilio/voice/<tenant_id>`} />
          <div className="mt-1 text-xs text-slate-500">{t("sip.inbound_help")}</div>
        </Field>
        <Field label={t("sip.status_callback")} full>
          <input value={s.twilio_status_callback_url || ""} onChange={set("twilio_status_callback_url")} className="input-dark font-mono" placeholder={`${backendBase}/api/twilio/status/<tenant_id>`} />
        </Field>
        <Field label={t("sip.recording_callback")} full>
          <input value={s.twilio_recording_status_callback_url || ""} onChange={set("twilio_recording_status_callback_url")} className="input-dark font-mono" placeholder={`${backendBase}/api/twilio/recording/<tenant_id>`} />
        </Field>
      </Section>

      <Section icon={Server} title={t("sip.sip_section")}>
        <Field label={t("sip.sip_enabled")}>
          <label className="flex items-center gap-2 text-slate-200">
            <input type="checkbox" checked={!!s.sip_enabled} onChange={setBool("sip_enabled")} />
            <span className="text-sm">{t("sip.sip_enabled_hint")}</span>
          </label>
        </Field>
        <Field label={t("sip.sip_uri")}>
          <input value={s.sip_uri || ""} onChange={set("sip_uri")} className="input-dark font-mono" placeholder="sip:1001@yourpbx.example.com" />
        </Field>
        <Field label={t("sip.sip_transport")}>
          <select value={s.sip_transport || "udp"} onChange={set("sip_transport")} className="input-dark font-mono">
            <option value="udp">udp</option>
            <option value="tcp">tcp</option>
            <option value="tls">tls</option>
          </select>
        </Field>
        <Field label={t("sip.outbound_proxy")}>
          <input value={s.sip_outbound_proxy || ""} onChange={set("sip_outbound_proxy")} className="input-dark font-mono" placeholder="proxy.example.com:5060" />
        </Field>
        <Field label={t("sip.from_user")}>
          <input value={s.sip_from_user || ""} onChange={set("sip_from_user")} className="input-dark font-mono" placeholder="caller-id" />
        </Field>
        <Field label={t("sip.auth_user")}>
          <input value={s.sip_auth_username || ""} onChange={set("sip_auth_username")} className="input-dark font-mono" placeholder="auth-user" />
        </Field>
        <Field label={t("sip.auth_pass")}>
          <input type="password" value={s.sip_auth_password || ""} onChange={set("sip_auth_password")} className="input-dark font-mono" />
        </Field>
      </Section>

      <Section icon={Shield} title={t("sip.notes_title")}>
        <div className="md:col-span-2 text-sm text-slate-400 leading-relaxed">
          {t("sip.notes")}
        </div>
      </Section>
    </form>
  );
}

